import plur from 'plur';
import type { DataIndexAttributeUsage } from '@/services/db/data-index/entity';
import type { DataIndexItemKind } from './types';
import { hasDesignStudioUsage, isObjectOrRelationshipAttribute } from './item';

/**
 * What a list row says about where it is used.
 *
 * The row has one narrow cell for an answer that can span seven surfaces, so
 * it shows the first two in reading order and names the rest in a tooltip.
 * Templates and
 * snippets share a chip because they share an icon and a reader thinks of
 * them together; the tooltip still counts them apart.
 */

/**
 * The counts a usage read answers with.
 *
 * A profile attribute's read is the widest of the three, and an object
 * attribute's and an event's fields are a subset of it, so one partial of
 * the generated contract covers all three — and a field the spec renames
 * stops compiling here rather than reading as zero.
 */
export type UsageCounts = Partial<DataIndexAttributeUsage>;

export type UsageChip = {
  icon: 'segment' | 'campaigns' | 'newsletter' | 'editor-code' | 'layout' | 'design-studio';
  count: number;
};

export type UsageSummary = {
  /** The first two surfaces in reading order, or fewer. Empty when nothing uses the row. */
  chips: UsageChip[];
  /** Every surface that uses it, named and counted. Empty when nothing does. */
  tooltip: string;
};

export type UsageSummaryTerms = {
  /** What the workspace calls a campaign, singular. */
  campaignSingular: string;
};

/** How many surfaces a row shows before the rest move into the tooltip. */
const MAX_CHIPS = 2;

const LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/**
 * Which surfaces a kind can be used by at all.
 *
 * A profile attribute or an event always shows the content surfaces; an
 * object or relationship attribute shows them only where the workspace has
 * them turned on. That is the page this replaces
 * (`app/components/data-index-list-table/usage-cell/index.gts:36-42`), and
 * the flag defaults off — so reading it for every kind would drop counts
 * Ember shows today.
 */
function surfacesFor(kind: DataIndexItemKind, withContent: boolean) {
  const content = isObjectOrRelationshipAttribute(kind) ? withContent : true;
  return { content, designStudio: hasDesignStudioUsage(kind, content) };
}

function countsFor(kind: DataIndexItemKind, counts: UsageCounts, withContent: boolean) {
  const applies = surfacesFor(kind, withContent);
  const read = (key: keyof UsageCounts, surfaceApplies = true) => (surfaceApplies ? (counts[key] ?? 0) : 0);

  return {
    segments: read('segment_count'),
    campaigns: read('campaign_count'),
    newsletters: read('newsletter_count'),
    templates: read('template_count', applies.content),
    snippets: read('snippet_count', applies.content),
    layouts: read('layout_count', applies.content),
    designStudio: read('design_studio_count', applies.designStudio),
  };
}

export function usageSummary(
  kind: DataIndexItemKind,
  counts: UsageCounts | undefined,
  terms: UsageSummaryTerms,
  withContent: boolean,
): UsageSummary {
  const n = countsFor(kind, counts ?? {}, withContent);

  const chips = (
    [
      { icon: 'segment', count: n.segments },
      { icon: 'campaigns', count: n.campaigns },
      { icon: 'newsletter', count: n.newsletters },
      { icon: 'editor-code', count: n.templates + n.snippets },
      { icon: 'layout', count: n.layouts },
      { icon: 'design-studio', count: n.designStudio },
    ] satisfies UsageChip[]
  ).filter((chip) => chip.count > 0);

  // The tooltip counts templates and snippets separately, because that is
  // what someone reading it is about to go and look for.
  const named: [number, string][] = [
    [n.segments, 'segment'],
    [n.campaigns, terms.campaignSingular],
    [n.newsletters, 'newsletter'],
    [n.templates, 'template'],
    [n.snippets, 'snippet'],
    [n.layouts, 'layout'],
    [n.designStudio, 'Design Studio entry'],
  ];
  const parts = named.filter(([count]) => count > 0).map(([count, noun]) => `${String(count)} ${plur(noun, count)}`);

  return { chips: chips.slice(0, MAX_CHIPS), tooltip: LIST.format(parts) };
}
